/**
 * Secure API helper — automatically attaches the Supabase JWT as
 * Authorization: Bearer <token> on every request.
 *
 * Usage:
 *   import { apiFetch } from '@/lib/api'
 *   const res = await apiFetch('/api/memory/list')
 *   const data = await apiFetch('/api/admin/users', { method: 'GET' })
 */
import { getSupabase } from '../hooks/useAuth'
import { API_BASE } from './config'

export async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const supabase = getSupabase()
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (token) {
      return { Authorization: `Bearer ${token}` }
    }
  } catch {
    // ignore — will fail with 401 on the backend
  }
  return {}
}

export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const authHeaders = await getAuthHeaders()
  const existingHeaders = (options.headers as Record<string, string>) ?? {}
  const finalUrl =
    typeof url === 'string' && url.startsWith('/api/')
      ? `${API_BASE}${url}`
      : url

  return fetch(finalUrl, {
    ...options,
    headers: {
      ...existingHeaders,
      ...authHeaders,
    },
  })
}
